/* global window */
import React from 'react';
import hoistNonReactStatic from 'hoist-non-react-statics';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import cookieManagerHelper from 'Helpers/cookies';
import setToastMessageState from 'Actions/setToastMessageState';
import Auth from '../../Auth';
import withTrack from 'HOCs/withTrack/withTrack';
import { getKYCReplyFeatureMetaData, getKYCReplyCount, isUserKYCEnabled, setKycReplyCount, getAPICallDelay } from 'Helpers/replyRestriction';
import { isEmpty } from 'Helpers/objects';
import withRouter from 'HOCs/withRouter';
import ImageWrapper from 'Components/ImageWrapper/ImageWrapper';
import UserKycPopup from 'ragnarok-web/dist/Components/UserKycPopup/UserKycPopup';
import ChatWrapper from '../../ChatWrapper';
import withConfig from 'HOCs/withConfig/withConfig';
import { ChatEventsEmitter } from 'ragnarok-web/dist/ChatEventsEmitter/ChatEventsEmitter';
import { getItem, setItem } from 'Helpers/sessionStorage';
import { IS_SELLER_KYC_POPUP_SHOWN } from 'ragnarok-web/dist/Constants/constants';

const withReplyRestriction = WrappedComponent => {
    class WithReplyRestriction extends React.Component {
        static displayName = `withReplyRestriction(${WrappedComponent.displayName || WrappedComponent.name})`;

        static propTypes = {
            replyRestrictionMetaData: PropTypes.object,
            setToastMessageState: PropTypes.func,
            threads: PropTypes.array,
            track: PropTypes.func,
            router: PropTypes.object,
            location: PropTypes.object,
            config: PropTypes.object
        };

        static defaultProps = {
            user: {},
            item: {},
            replyRestrictionMetaData: {},
            threads: [],
            track: () => {}
        }

        constructor(props) {
            super(props);
            this.kycReplyCount = 0;
            this.kycMetaData = {};
            this.kycSellerMetaData = {};

            this.state = {
                isKycReplyRestricted: false
            };
            this.chatConfig = props.config
                && typeof props.config.get === 'function'
                && props.config.get('chat').newChat;
            this.events = [];
        }

        componentDidMount() {
            this.initKycData();
            window.addEventListener('popstate', this.handleKycModalPopState);
            this.attachEvents();
        }

        componentWillUnmount() {
            window.removeEventListener('popstate', this.handleKycModalPopState);
            this.detachEvents();
        }

        initKycData = () => {
            this.kycReplyCount = getKYCReplyCount();
            this.kycMetaData = getKYCReplyFeatureMetaData();
            this.kycSellerMetaData = getKYCReplyFeatureMetaData(true);
            this.formatMetaData();
        }

        attachEvents = () => {
            this.events = [
                ChatEventsEmitter.subscribe('incrementKycReplyCount', this.incrementKycReplyCount)
            ];
        }

        detachEvents = () => this.events.forEach(unsub => unsub());

        formatMetaData = () => {
            try {
                // Buyer
                if (this.kycMetaData.soft) {
                    this.kycMetaData.soft = Number(this.kycMetaData.soft);
                }
                if (this.kycMetaData.hard) {
                    this.kycMetaData.hard = Number(this.kycMetaData.hard);
                }
                if (this.kycMetaData.time) {
                    this.kycMetaData.time = Number(this.kycMetaData.time);
                }

                // Seller
                if (this.kycSellerMetaData.soft) {
                    this.kycSellerMetaData.soft = Number(this.kycSellerMetaData.soft);
                }
                if (this.kycSellerMetaData.hard) {
                    this.kycSellerMetaData.hard = Number(this.kycSellerMetaData.hard);
                }
                if (this.kycSellerMetaData.time) {
                    this.kycSellerMetaData.time = Number(this.kycSellerMetaData.time);
                }
            }
            // eslint-disable-next-line no-empty
            catch (error) {}
        }

        incrementKycReplyCount = (itemId, itemUserId) => {
            if (this.checkIfKycRestrictUser(itemId, itemUserId) || this.checkIfKycRestrictSellerUser()) {
                ++this.kycReplyCount;
                setKycReplyCount(this.kycReplyCount, this.kycMetaData.time);
            }
        }

        handleKycModalPopState = () => {
            if (this.continueToChatCbEnabled) {
                this.continueToChatCb();
            }
            delete this.continueToChatCb;
            delete this.continueToChatCbEnabled;
        }

        getReplyRestrictedValue = () => {
            const replyRestrictionCookie = cookieManagerHelper.readCookie('reply_restriction');

            return !!(replyRestrictionCookie && (replyRestrictionCookie === 'true'));
        }

        showReplyRestrictionMessage = () => {
            const replyRestrictionErrorText = this.props.replyRestrictionMetaData.error_text;

            this.props.setToastMessageState({
                isOpen: true,
                message: replyRestrictionErrorText,
                expiration: 5000
            });
        }

        isNewConversation = (itemId, userId) => {
            return !this.props.threads.filter(thread => thread.userId === userId && thread.itemId === itemId).length > 0;
        }

        getKycMetaData = () => {
            const sellerExpFlag = isUserKYCEnabled(true);
            const buyerExpFlag = isUserKYCEnabled();

            if (buyerExpFlag && !sellerExpFlag) {
                return this.kycMetaData;
            }
            else if (!buyerExpFlag && sellerExpFlag) {
                return this.kycSellerMetaData;
            }

            return ((this.kycSellerMetaData && this.kycSellerMetaData.hard) > (this.kycMetaData && this.kycMetaData.hard)
                ? this.kycSellerMetaData
                : this.kycMetaData);
        }

        isKycReplyRestricted = () => {
            const kycMetaData = this.getKycMetaData();

            this.kycReplyCount = getKYCReplyCount();

            if (this.kycReplyCount === undefined) { // expired reply count
                this.kycReplyCount = 0;
                this.getUserPreferenceData();
            }

            if (this.kycReplyCount >= kycMetaData.soft) {
                this.isSoftBreach = true;
                if (this.kycReplyCount >= kycMetaData.hard) {
                    this.isSoftBreach = false;
                    this.getUserPreferenceData();
                }
                return true;
            }
            return false;
        }

        getUserPreferenceData = () => {
            const XMPPClient = ChatWrapper.XMPP;
            const params = {
                config: this.chatConfig
            };
            const delay = getAPICallDelay();

            XMPPClient.getInstance(params).then(instance => {
                setTimeout(() => {
                    instance.getUserPreference();
                }, delay);
            });
        };

        showKYCPopup = cb => {
            this.continueToChatCb = cb;
            this.props.router.push(`${this.props.location.pathname}#kyc`);
        };

        showSellerKYCPopup = cb => {
            this.continueToChatCb = cb;
            this.props.router.push(`${this.props.location.pathname}#sellerKyc`);
        };

        checkIfKycPopupRoute = () => this.props.location.hash && this.props.location.hash.indexOf('kyc') !== -1;

        checkIfSellerKycPopupRoute = () => this.props.location.hash && this.props.location.hash.indexOf('sellerKyc') !== -1;

        checkIfKycRestrictUser = (itemId, itemUserId) => !isEmpty(this.kycMetaData) && isUserKYCEnabled() && this.isNewConversation(itemId, itemUserId);

        checkIfKycRestrictSellerUser = () => !isEmpty(this.kycSellerMetaData) && isUserKYCEnabled(true);

        isReplyRestricted = (itemId, itemUserId, cb = () => {}, fromItemPage = false, isMakeOffer = false) => {
            let isRestricted = false;
            const user = Auth.getUser();

            this.itemId = itemId;
            this.sellerId = itemUserId;
            this.userId = user && user.id;

            if (this.checkIfKycRestrictUser(itemId, itemUserId) && fromItemPage) {
                if (this.isKycReplyRestricted()) {
                    isRestricted = true;
                }

                if (isRestricted) {
                    this.isMakeOfferTapped = isMakeOffer;
                    this.showKYCPopup(cb);
                }
                return isRestricted;
            }

            if (user && this.props.replyRestrictionMetaData && Object.values(this.props.replyRestrictionMetaData).length
                && itemUserId !== user.id) {
                if (this.isNewConversation(itemId, itemUserId)) {
                    isRestricted = this.getReplyRestrictedValue();
                }
            }

            if (isRestricted) {
                this.showReplyRestrictionMessage();
                this.props.track('restricted_spam_chat', {
                    item_id: itemId,
                    user_id: user.id,
                    date: new Date()
                });
            }
            return isRestricted;
        }

        isSellerReplyRestricted = (cb = () => {}) => {
            if (!getItem(IS_SELLER_KYC_POPUP_SHOWN) && this.checkIfKycRestrictSellerUser() && this.isKycReplyRestricted()) {
                setItem(IS_SELLER_KYC_POPUP_SHOWN, true);
                this.showSellerKYCPopup(cb);
            }
        }

        getConversationCountLeft = () => this.getKycMetaData().hard - this.kycReplyCount;

        continueToChatTapped = tappedAt => {
            if (tappedAt === 'CTA_CLICKED') {
                this.continueToChatCbEnabled = true;
            }
        }

        render() {
            return (<React.Fragment>
                { (this.checkIfKycPopupRoute() || this.checkIfSellerKycPopupRoute()) && this.continueToChatCb
                    && <UserKycPopup
                        conversationCountLeft={ this.getConversationCountLeft() }
                        isKycSoftBreach={ this.isSoftBreach }
                        item_id={ this.itemId }
                        buyer_id={ this.userId }
                        seller_id={ this.sellerId }
                        track={ this.props.track }
                        continueToChatTapped={ this.continueToChatTapped }
                        ImageWrapper={ ImageWrapper }
                        isMakeOfferTapped={ this.isMakeOfferTapped }
                        customRouter={ this.props.router }
                        isSeller={ this.checkIfSellerKycPopupRoute() }
                    /> }
                <WrappedComponent
                    { ...this.props }
                    isReplyRestricted={ this.isReplyRestricted }
                    isSellerReplyRestricted={ this.isSellerReplyRestricted }
                    incrementKycReplyCount={ this.incrementKycReplyCount }
                />
            </React.Fragment>);
        }
    }
    const mapStateToProps = state => ({
        replyRestrictionMetaData: state.chat && state.chat.replyRestrictionMetaData,
        kycRestrictionMetaData: state.chat && state.chat.kycRestrictionMetaData,
        threads: state.chat && state.chat.threads || []
    });
    const mapDispatchToProps = dispatch => ({
        setToastMessageState: state => dispatch(setToastMessageState(state))
    });

    return hoistNonReactStatic(
        compose(
            withRouter,
            withConfig,
            withTrack,
            connect(mapStateToProps, mapDispatchToProps)
        )(WithReplyRestriction),
        WrappedComponent
    );
};

export default withReplyRestriction;

